import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { classNames } from '../../utils/format';
import { useT } from '../../i18n/LocaleContext';

const MAX_RESULTS = 8;

function fullName(member) {
  return `${member.firstName} ${member.lastName}`.trim();
}

export default function PayerCombobox({
  id,
  members,
  value,
  onChange,
  invalid,
  placeholder,
  autoFocus,
}) {
  const t = useT();
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    if (value?.memberId) {
      const member = members.find((m) => m.id === value.memberId);
      setText(member ? fullName(member) : value.payerName || '');
    } else {
      setText(value?.payerName || '');
    }
  }, [value?.memberId, value?.payerName, members]);

  useEffect(() => {
    if (!autoFocus) return;
    inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const trimmed = text.trim();
  const lowered = trimmed.toLowerCase();

  const filtered = useMemo(() => {
    if (!lowered) return members.slice(0, MAX_RESULTS);
    return members
      .filter((m) => {
        const haystack = `${fullName(m)} ${m.phone || ''} ${m.code || m.id || ''}`.toLowerCase();
        return haystack.includes(lowered);
      })
      .slice(0, MAX_RESULTS);
  }, [members, lowered]);

  const exactMatch = useMemo(
    () => filtered.find((m) => fullName(m).toLowerCase() === lowered),
    [filtered, lowered]
  );

  const showGuestOption = trimmed.length > 0 && !exactMatch;
  const totalOptions = filtered.length + (showGuestOption ? 1 : 0);

  useEffect(() => {
    setHighlight(0);
  }, [text, open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${highlight}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const selectMember = useCallback(
    (member) => {
      onChange({ memberId: member.id, payerName: '' });
      setText(fullName(member));
      setOpen(false);
    },
    [onChange]
  );

  const selectGuest = useCallback(
    (name) => {
      const guestName = name.trim();
      if (!guestName) return;
      onChange({ memberId: '', payerName: guestName });
      setOpen(false);
    },
    [onChange]
  );

  const handleChange = (e) => {
    const next = e.target.value;
    setText(next);
    setOpen(true);
    const trimmedNext = next.trim();
    const match = members.find((m) => fullName(m).toLowerCase() === trimmedNext.toLowerCase());
    if (match) {
      onChange({ memberId: match.id, payerName: '' });
    } else {
      onChange({ memberId: '', payerName: trimmedNext });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => Math.min(totalOptions - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      if (highlight < filtered.length) {
        const member = filtered[highlight];
        if (member) selectMember(member);
      } else if (showGuestOption) {
        selectGuest(trimmed);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const isGuest = !value?.memberId && Boolean(value?.payerName);
  const listboxId = `${id || 'payer'}-listbox`;

  return (
    <div ref={wrapRef} className={classNames('combobox', open && 'is-open')}>
      <div className="combobox-input-wrap">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={text}
          onChange={handleChange}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className={classNames('form-control', invalid && 'is-invalid')}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listboxId}
          role="combobox"
        />
        {isGuest && <span className="combobox-guest-tag">{t('payments.payer_guest_label')}</span>}
      </div>

      {open && (
        <div id={listboxId} className="combobox-popover" role="listbox" ref={listRef}>
          {filtered.length === 0 && !showGuestOption && (
            <div className="combobox-empty">{t('payments.payer_search_empty')}</div>
          )}
          {filtered.map((member, idx) => (
            <div
              key={member.id}
              data-index={idx}
              role="option"
              aria-selected={highlight === idx}
              className={classNames('combobox-option', highlight === idx && 'is-highlighted', value?.memberId === member.id && 'is-selected')}
              onMouseDown={(e) => { e.preventDefault(); selectMember(member); }}
              onMouseEnter={() => setHighlight(idx)}
            >
              <div className="combobox-option-main">
                <span className="combobox-option-name">{fullName(member)}</span>
                <span className="combobox-option-meta">{member.phone || member.code || member.id}</span>
              </div>
              <span className="combobox-option-id mono">{member.code || member.id}</span>
            </div>
          ))}
          {showGuestOption && (
            <div
              data-index={filtered.length}
              role="option"
              aria-selected={highlight === filtered.length}
              className={classNames('combobox-option combobox-option--guest', highlight === filtered.length && 'is-highlighted')}
              onMouseDown={(e) => { e.preventDefault(); selectGuest(trimmed); }}
              onMouseEnter={() => setHighlight(filtered.length)}
            >
              <span className="combobox-option-plus" aria-hidden>＋</span>
              <span className="combobox-option-name">{t('payments.payer_guest_action', { name: trimmed })}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
